import { inject, Inject, Injectable } from '@angular/core';
import { Dexie, Table } from 'dexie';
import { InjectionToken } from '@angular/core';
import { FiltersConfig, HttpCoreService } from '@dataclouder/ngx-core';
import { LangWord } from 'src/app/core/lexicore/lexicore.classes';

export const CORE_TABLES_TOKEN = new InjectionToken<string[]>('CORE_TABLES_TOKEN');
export const CORE_INDEX_TOKEN = new InjectionToken<string>('CORE_INDEX_TOKEN');

interface QueryCore {
  collection: string;
  filters: FiltersConfig;
}

const Endpoints = {
  GetKnowledgePath: 'api/knowledge/knowledge-path',
  QueryCoreKnowledge: 'api/core-knowledge/query',
};

/**
 * 🤔 Its not clear all the responsabilities this core knowledge service has.
 * Ill start with easy.
 * 1) ✅ Interact with the indexedDB database and tables corresponding to core knowledge.
 * 2) ✅ Get knowledge data by path and extract specific data from the response.
 * 3) ❌ It may save in Memory but for now will delegated to client service. until i understand more.
 */

@Injectable({
  providedIn: 'root',
})
export class CoreKnowledgeService extends Dexie {
  // Services
  private httpCoreService = inject(HttpCoreService);

  // Map to hold tables for each category
  private categoryTables: Map<string, Table<LangWord, string>> = new Map();

  constructor(@Inject(CORE_TABLES_TOKEN) tables: string[], @Inject(CORE_INDEX_TOKEN) index: string) {
    super('KnowledgeCoreDB'); // Database name

    const storesConfig: { [tableName: string]: string | null } = {};

    tables.forEach((table) => {
      storesConfig[table] = index || 'word'; // Primary key 'concept', index 'conceptPath'
    });

    this.version(1).stores(storesConfig);

    // Initialize table references
    tables.forEach((table) => {
      const tableName = `${table}`;
      this.categoryTables.set(table, this.table(tableName));
    });
  }

  /**
   * Retrieves the Dexie table for a given category.
   * @param category The category string.
   * @returns The Dexie Table instance.
   * @throws Error if the category is invalid or not configured.
   */
  private getTable(category: string): Table<LangWord, string> {
    const table = this.categoryTables.get(category);
    if (!table) {
      throw new Error(`CoreKnowledgeService: No table found for category "${category}". Ensure this category was provided during service initialization.`);
    }
    return table;
  }

  async getKnowledgeData(table: string, concept: string): Promise<LangWord | undefined> {
    const tableDexie = this.getTable(table);
    return tableDexie.get(concept);
  }

  async getAllKnowledgeCoreData(category: string): Promise<LangWord[]> {
    const table = this.getTable(category);
    return table.toArray();
  }

  async getAllKnowledgeCoreDataAsObject(category: string): Promise<{ [concept: string]: LangWord }> {
    const table = this.getTable(category);

    const statesArray = await table.toArray();
    return statesArray.reduce((acc, state) => {
      acc[state.word] = state;
      return acc;
    }, {} as { [concept: string]: LangWord });
  }

  async addOrUpdateKnowledgeCoreData(category: string, state: LangWord): Promise<string> {
    const table = this.getTable(category);
    return table.put(state);
  }

  async bulkAddOrUpdateKnowledgeCoreData(category: string, states: LangWord[]): Promise<string[]> {
    const table = this.getTable(category);
    await table.bulkPut(states);
    return states.map((s) => s.word);
  }

  async deleteKnowledgeUserState(category: string, concept: string): Promise<void> {
    const table = this.getTable(category);
    return table.delete(concept);
  }

  /**
   * Clears all knowledge user state data for ALL categories.
   */
  async clearAllData(): Promise<void> {
    const clearPromises: Promise<void>[] = [];
    this.categoryTables.forEach((table) => {
      clearPromises.push(table.clear());
    });
    await Promise.all(clearPromises);
  }

  /**
   * Clears all knowledge user state data for a specific category.
   * @param category The category for which to clear data.
   */
  async clearDataForCategory(category: string): Promise<void> {
    const table = this.getTable(category);
    await table.clear();
  }

  async queryCoreKnowledge(queryCore: QueryCore): Promise<LangWord[]> {
    const response = await this.httpCoreService.post(`${Endpoints.QueryCoreKnowledge}`, queryCore);
    return response;
  }
}
